import { useCallback, useState } from 'react'

import { vrList } from '@/data/vrList'

/**
 * 動画をcacheするため、事前に全取得する
 */
export function usePredownloadVideos() {
  const [loading, setLoading] = useState(false)
  const [cachedList, setCachedList] = useState<string[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [messages, setMessages] = useState<string[]>([])

  /**
   * 全動画の事前ダウンロード
   */
  const predownloadVideos = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMessages(['Start downloading...'])
    try {
      const videoSrcList = vrList
        .flatMap((vr) => [vr.vrSrc || ''])
        .filter((src) => src !== '')
        .reverse()
      // 重複排除
      const srcList = [...new Set(videoSrcList)]
      const cached = []
      for (const src of srcList) {
        const response = await fetch(src, { cache: 'reload' })
        if (response.ok) {
          try {
            const root = await navigator.storage.getDirectory()
            const filename = src.split('/').pop()
            if (!filename) {
              throw new Error('filename is null')
            }
            const fileHandle = await root.getFileHandle(filename, {
              create: true,
            })
            const writable = await fileHandle.createWritable()
            if (!response.body) {
              throw new Error('response body is null')
            }
            await response.body.pipeTo(writable)
            setMessages((before) => [
              ...before,
              `${before.length}/${srcList.length} ${src} done.`,
            ])
            cached.push(src)
          } catch (srcError) {
            setMessages((before) => [
              ...before,
              `${before.length}/${srcList.length} ${src} error: ${(srcError as Error).message}`,
            ])
            setError(
              new Error(
                `${error?.message ?? ''}\n${(srcError as Error).message}`,
              ),
            )
          }
        } else {
          setMessages((before) => [
            ...before,
            `${before.length}/${srcList.length} ${src} error!`,
          ])
          setError(new Error(`${error?.message ?? ''}\n${src}`))
        }
      }
      setCachedList(cached)
      setMessages((before) => [...before, `process done.`])
    } catch (caughtError) {
      setError(caughtError as Error)
    } finally {
      setLoading(false)
    }
  }, [vrList, setError, setLoading])

  const deleteVideos = useCallback(async () => {
    if (!window.confirm('ローカルのVideoを削除しますか？')) {
      return
    }
    const root = await navigator.storage.getDirectory()
    for await (const [name] of (root as any).entries() as AsyncIterable<
      [string, FileSystemHandle]
    >) {
      await root.removeEntry(name, { recursive: true })
    }
  }, [])

  return {
    cachedList,
    error,
    messages,
    loading,
    predownloadVideos,
    deleteVideos,
  }
}
